import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { IC_ROLES } from '../../../base/conference/icRoles';
import { IconChatUnread, IconHandHoldingHand, IconShare } from '../../../base/icons/svg';
import Label from '../../../base/label/components/web/Label';
import { getLocalParticipant, getParticipantWithICRoleAndPartner, getRemoteParticipants, participantHasRole } from '../../../base/participants/functions';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { createBreakoutRoom, moveToRoom, sendParticipantToRoom } from '../../../breakout-rooms/actions';
import { getBreakoutRooms } from '../../../breakout-rooms/functions';
import { openChat } from '../../../chat/actions.web';
import { isBreakOutRoomHandled, setBreakOutRoomHandled, setBreakOutRoomUnhandled } from '../../../inklusiva/rolematching/functions';
/**
 * Label for the conference name.
 *
 * @param {IProps} props - The props of the component.
 * @returns {ReactElement}
 */
const AssistantRelationLabel = ({ _breakoutRooms, _localParticipant, _remoteParticipants, _isBreakoutRoomHandled, _participantHasRole, _connectionState, _getParticipantWithICRoleAndPartner }) => {
    const dispatch = useDispatch();
    let labelText;
    let tooltipText = '';
    let visible = false;
    let otherParticipant;
    const localId = _localParticipant?.id;
    const localJid = _connectionState?.connection?.getJid();
    if (_participantHasRole(localId, IC_ROLES.ASSISTED)) {
        const rolePartner = _getParticipantWithICRoleAndPartner(IC_ROLES.ASSISTANT, _localParticipant ? localId : '');
        if (rolePartner === undefined) {
            tooltipText = 'Warte auf Assistenz';
            labelText = 'Warte auf Assistenz';
        }
        else {
            labelText = rolePartner?.name ?? '';
            tooltipText = `${labelText} assistiert mir`;
        }
        visible = true;
        otherParticipant = rolePartner;
    }
    if (_participantHasRole(localId, IC_ROLES.ASSISTANT)) {
        const assistantRoleDefinition = (_localParticipant?.icRoles ?? []).find(role => role.name === IC_ROLES.ASSISTANT);
        if (_participantHasRole(assistantRoleDefinition?.partner ?? undefined, IC_ROLES.ASSISTED)) {
            const assistedParticipant = _remoteParticipants?.get(assistantRoleDefinition?.partner ?? '');
            tooltipText = `Ich assistiere ${assistedParticipant?.name}`;
            labelText = assistedParticipant?.name ?? '';
            visible = true;
            otherParticipant = assistedParticipant;
        }
    }
    /**
     * OnClick handler for opening the private chat between the two participants.
     *
     * @returns {void}
     */
    const openPrivateChat = () => {
        dispatch(openChat(otherParticipant));
    };
    const breakoutRoomName = otherParticipant
        ? `${otherParticipant.name} - ${_localParticipant?.name}`
        : '';
    useEffect(() => {
        // iterate over all breakout rooms and check if there is a breakout room with the name
        const breakoutRoom = Object.values(_breakoutRooms).find(room => room.name === breakoutRoomName);
        // check if there is a participant with the localId in the breakout room
        if (!_isBreakoutRoomHandled && breakoutRoom && otherParticipant && _localParticipant) {
            const localParticipantInRoom = Object.values(breakoutRoom.participants).find(participant => participant.jid === localJid);
            const otherParticipantInRoom = Object.values(breakoutRoom.participants).find(participant => otherParticipant && participant.jid.startsWith(otherParticipant.id));
            if (!otherParticipantInRoom && otherParticipant) {
                dispatch(sendParticipantToRoom(otherParticipant.id, breakoutRoom.id));
            }
            if (!localParticipantInRoom && _localParticipant) {
                dispatch(moveToRoom(breakoutRoom.jid));
            }
            // set handled flag if local and remote participant are in the breakout room
            localParticipantInRoom && otherParticipantInRoom && dispatch(setBreakOutRoomHandled());
        }
    }, [_breakoutRooms]);
    const openBreakoutRoom = () => {
        dispatch(setBreakOutRoomUnhandled());
        dispatch(createBreakoutRoom(breakoutRoomName));
    };
    const buttons = otherParticipant ? (React.createElement(React.Fragment, null,
        React.createElement(Button, { className: 'infobarButton mr-05', icon: IconChatUnread, label: 'Private Nachricht', 
            // eslint-disable-next-line react/jsx-no-bind
            onClick: openPrivateChat, size: 'small', type: BUTTON_TYPES.SECONDARY }),
        React.createElement(Button, { className: 'infobarButton mr-05', icon: IconShare, label: 'Breakout-Raum hinzufügen', 
            // eslint-disable-next-line react/jsx-no-bind
            onClick: openBreakoutRoom, size: 'small', type: BUTTON_TYPES.SECONDARY }))) : React.createElement(React.Fragment, null);
    return visible ? (React.createElement(React.Fragment, null,
        React.createElement(Tooltip, { content: tooltipText, position: 'bottom' },
            React.createElement(Label, { accessibilityText: tooltipText, className: 'icLabelTransparent', icon: IconHandHoldingHand, iconColor: '#fff', id: 'assistantRelationLabel', text: tooltipText })),
        buttons))
        : React.createElement(React.Fragment, null);
};
const mapStateToProps = (state) => {
    return {
        _breakoutRooms: Object.values(getBreakoutRooms(state)),
        _connectionState: state['features/base/connection'],
        _remoteParticipants: getRemoteParticipants(state),
        _localParticipant: getLocalParticipant(state),
        _isBreakoutRoomHandled: isBreakOutRoomHandled(state),
        _participantHasRole: (participantId, role) => participantHasRole(state, participantId, role),
        _getParticipantWithICRoleAndPartner: (role, partnerId) => getParticipantWithICRoleAndPartner(state, role, partnerId)
    };
};
export default connect(mapStateToProps)(AssistantRelationLabel);
